import { ErrorList, SectionTitle, TableStyle, TableTitle, Title } from '../../../style';
import { tableSections as defaultTableSections } from '../../../consts/tableSections';
import React, { useCallback, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IoIosArrowDown } from 'react-icons/io';
import { IoIosArrowUp } from 'react-icons/io';
import { Errors } from '../../ReportTables/style';
import { FundErrorsDisplay } from '../../../../components/FundErrorsDisplay';
import { Warning } from './style';
import { idCreator } from '../../../../../Project/utils/idCreator';

export const AIF002Reports = ({
  tableSections = defaultTableSections,
  isStaticTemplate,
  funds,
  currentDataKey,
  onChangeFund,
  schema,
  addRow,
  onDeleteRow,
  isMultiFunds,
  sectionRefs,
  onChangeActiveSection,
  onScrollingChange,
  virtuosoRef,
  isViewer
}) => {
  const [expandIds, setExpandIds] = useState([]);

  // Horizontal scroll sync across section tables.
  // `containerRef` scopes the lookup to this report; `scrollLeftRef` holds the
  // shared position. We sync via a single rAF-throttled handler over the few
  // tables Virtuoso keeps mounted — no per-element listeners, observers, or timers.
  const containerRef = useRef(null);
  const scrollLeftRef = useRef(0);
  const isSyncingRef = useRef(false);

  const handleTableScroll = useCallback(e => {
    const { scrollLeft } = e.currentTarget;
    // Ignore the echo from programmatically syncing the other tables.
    if (scrollLeft === scrollLeftRef.current) return;
    scrollLeftRef.current = scrollLeft;
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
      const tables = containerRef.current?.querySelectorAll('.scrolling-table');
      tables?.forEach(table => {
        if (table.scrollLeft !== scrollLeftRef.current) {
          table.scrollLeft = scrollLeftRef.current;
        }
      });
    });
  }, []);

  const renderSection = (index, onOpen, ids) => {
    const {
      key,
      title,
      Component = null,
      hasAddDelete,
      subTitle,
      sectionTitle
    } = tableSections[index];

    const isRequiredTable =
      funds[currentDataKey]?.required_tables?.[key]?.field_names?.length === 0;

    const errors = funds[currentDataKey]?.fund_data?.[key]?.find(item => item?.errors?.general)
      ?.errors?.general;

    const errorsWithFunds = Object.values(funds)
      .map(item => ({
        name: item.fund_name,
        errors: item?.fund_data?.[key]?.find(item => item?.errors?.general)?.errors?.general,
        isRequiredTable: item?.required_tables?.[key]?.field_names?.length === 0,
        fund_id: item.fund_id
      }))
      .filter(value => !!value.errors || !!value.isRequiredTable);

    return (
      <div key={key} ref={el => (sectionRefs.current[key] = el)} id={`aif002-section-${key}`}>
        {sectionTitle && (
          <SectionTitle id={`aif002-section-title-${key}`}>{sectionTitle}</SectionTitle>
        )}
        {title && (
          <TableTitle
            onClick={() => {
              if (!!Component && !subTitle) onOpen(key);
            }}
            id={key}
          >
            {!!Component && !subTitle ? (
              !ids.includes(key?.replace(/\d+/g, '')) ? (
                <IoIosArrowDown fontSize="16px" />
              ) : (
                <IoIosArrowUp fontSize="16px" />
              )
            ) : (
              ''
            )}
            {title}
          </TableTitle>
        )}

        {subTitle && (
          <TableTitle onClick={() => onOpen(key)} id={key}>
            {!ids.includes(key) ? (
              <IoIosArrowDown fontSize="16px" />
            ) : (
              <IoIosArrowUp fontSize="16px" />
            )}
            {subTitle}
          </TableTitle>
        )}

        {!ids.includes(key) && !isMultiFunds && (
          <Errors id={`aif002-section-errors-${key}`}>
            {isRequiredTable && (
              <li id={`aif002-section-required-error-${key}`}>This section must be completed</li>
            )}
            {errors?.map(item => (
              <li key={item.msg} id={`aif002-section-error-${key}-${idCreator(item.msg)}`}>
                {item.msg}
              </li>
            ))}
          </Errors>
        )}

        {!ids.includes(key?.replace(/\d+/g, '')) && !!Component && (
          <TableStyle
            id={`aif002-section-table-${key}`}
            style={{ position: 'relative' }}
            className="prevent-split scrolling-table"
            onScroll={handleTableScroll}
            ref={el => {
              // Align a freshly-mounted table with the shared scroll position.
              if (el) el.scrollLeft = scrollLeftRef.current;
            }}
          >
            {!ids.includes(key) && !isViewer && isMultiFunds && errorsWithFunds?.length > 0 && (
              <FundErrorsDisplay funds={funds} errorsWithFunds={errorsWithFunds} />
            )}
            <Component
              currentDataKey={currentDataKey}
              funds={funds}
              onChangeFund={onChangeFund}
              schema={schema}
              isStaticTemplate={isStaticTemplate}
              isMultiFunds={isMultiFunds}
              onAddColumn={hasAddDelete ? addRow : undefined}
              onDeleteColumn={hasAddDelete ? onDeleteRow : undefined}
            />
          </TableStyle>
        )}
        {index === tableSections.length - 1 && (
          <Warning id="aif002-warning-note">
            <span id="aif002-warning-asterisk">*</span> additional information that National
            Competent Authorities could require AIFMs to report on a periodic basis pursuant to the
            Opinion published by ESMA on &quot;Collection of information for the effective
            monitoring of systemic risk under Article 24(5), first sub-paragraph, of the
            AIFMD&quot;. This information becomes mandatory if it is required by the National
            Competent Authority to which the AIF reporting file is reported.
          </Warning>
        )}
      </div>
    );
  };

  const onOpenTable = id => {
    if (expandIds.includes(id)) {
      setExpandIds(prev => prev.filter(val => val !== id));
    } else {
      setExpandIds(prev => [...prev, id]);
    }
  };

  const onChangeIndex = value => {
    const currentSection = tableSections[value.startIndex];
    if (currentSection) {
      onChangeActiveSection(currentSection.key);
    }
  };

  return (
    <div
      className="tables k-grid"
      id={isStaticTemplate ? 'static-table' : 'pdfContainer'}
      ref={containerRef}
      style={{
        height: isStaticTemplate
          ? isMultiFunds
            ? 'calc(100% - 60px)'
            : 'calc(100% - 114px)'
          : isMultiFunds
            ? 'calc(100% - 24px)'
            : 'calc(100% - 84px)'
      }}
    >
      {funds[currentDataKey]?.report_status?.errors?.length > 0 && (
        <ErrorList id="aif002-report-status-errors">
          <IoIosArrowUp id="aif002-report-status-toggle-icon" />
          <div className="body" id="aif002-report-status-body">
            <div className="header" id="aif002-report-status-header">
              <p className="title" id="aif002-report-status-title">
                Submission status:
              </p>
              <p
                className="status"
                id="aif002-report-status-badge"
                style={{
                  background: '#DF4141'
                }}
              >
                Not submitted
              </p>
            </div>
            <ul id="aif002-report-status-list">
              {funds?.[currentDataKey]?.report_status?.errors?.map(item => (
                <li
                  key={item.reference_key}
                  id={`aif002-report-status-error-${idCreator(item.reference_key)}`}
                >
                  <span
                    className="underline"
                    id={`aif002-report-status-reference-${idCreator(item.reference_key)}`}
                  >
                    Reference: {item.reference_key}
                  </span>
                  , Error message:{' '}
                  <i id={`aif002-report-status-message-${idCreator(item.reference_key)}`}>
                    {item.error_message}
                  </i>
                </li>
              ))}
            </ul>
          </div>
        </ErrorList>
      )}
      <Title id="report_title" style={{ display: 'none' }}>
        AIF002 - Report on an AIF
      </Title>

      <Virtuoso
        style={{
          height: '100%',
          width: '100%'
        }}
        rangeChanged={onChangeIndex}
        isScrolling={onScrollingChange}
        totalCount={tableSections.length}
        itemContent={index => renderSection(index, onOpenTable, expandIds)}
        overscan={3}
        ref={virtuosoRef}
      />
    </div>
  );
};
